import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { NavLink } from "react-router-dom";
import Moment from "react-moment";
import moment from "moment";
import { useReactToPrint } from "react-to-print";

//pages & includes
import AccountsSidebar from "../AccountsSidebar";
import AccountPrint from "./AccountPrint";
import ViewAccountSheet from "./ViewAccountSheet";

//functions
import { getCookie } from "../../../../../functions/Functions";

import { useTranslation } from "react-i18next";
import { MdDelete } from "react-icons/md";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";

import "react-confirm-alert/src/react-confirm-alert.css";
import "react-datepicker/dist/react-datepicker.css";

//context consumer
import { UserContext } from "../../../../../contexts/User";
import DataTable from "../../../../common/DataTable";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";

const BankReceipt = () => {
  const { t } = useTranslation();

  const { authUserInfo } = useContext(UserContext);

  const [bankPaymentReceiptList, setBankPaymentReceiptList] = useState(null);
  const [bankPaymentReceiptListForSearch, setBankPaymentReceiptListForSearch] =
    useState(null);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  //search result
  let [searchedBPList, setSearchedBPList] = useState({
    list: null,
    searched: false,
  });

  const componentRef = useRef("");
  const searchInputRef = useRef(null);

  //print satate
  const [bankPayment, setBankPayment] = useState({
    doc_date: new Date(),
    transactionAccount: "",
    doc_no: null,
    branch_name: "",
    customer_name: "",
    remarks: "",
  });

  const [accountLedger, setAccountLedger] = useState({
    modal: false,
    ledger: null,
  });

  //Totals of rows
  const [totalRowAmount, setTotalRowAmount] = useState(0);
  const [totalRowWhtAmount, setTotalRowWhtAmount] = useState(0);
  const [totalRowTaxAmount, setTotalRowTaxAmount] = useState(0);
  const [totalRowNetAmount, setTotalRowNetAmount] = useState(0);
  const [newRow, setNewRow] = useState([]);

  useEffect(() => {
    getBPReciptList(1);
  }, []);

  const getBPReciptList = async (pageNo) => {
    setIsFetchingData(true);

    const url = BASE_URL + "/settings/bank-receipt?page=" + pageNo;

    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });

      setBankPaymentReceiptList(res.data[0]);
      setBankPaymentReceiptListForSearch(res.data[1]);
      setIsFetchingData(false);
    } catch (error) {
      setIsFetchingData(false);
    }
  };

  const handleOnlyPrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
        @page {
          size: landscape;
          margin: 15px;
        }
        body {
          -webkit-print-color-adjust: exact;
        }
      `,
  });

  const handlePrint = async (item) => {
    setIsFetchingData(true);

    const url = BASE_URL + "/settings/bank-receipt/" + item.id;

    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });

      const dateFormat = new Date(res?.data?.document_date);

      let bankReceiptDetail = []

      if (res?.data?.bankreceiptdetail) {
        res.data.bankreceiptdetail.forEach((item) => {
          bankReceiptDetail.push({
            level3_name: item.coaname?.level3_name,
            id: item.id,
            account_name: item.account_name,
            cheque_date: item.cheque_date,
            cheque_no: item.cheque_no,
            amount: item.amount,
            remarks: item.remarks,
            other_tax_amount: item.other_tax_amount,
            wht_amount: item.wht_amount,
            net_amount: item.net_amount,
          });
        });
      }

      setNewRow(bankReceiptDetail);

      setBankPayment({
        doc_date: dateFormat,
        doc_no: res.data?.document_identity,
        branch_name: res?.data?.branch?.name,
        customer_name: res?.data?.customer?.name,
        transactionAccount: res?.data?.transaction_name?.level3_name,
        remarks: res?.data?.remarks,
      });

      setTotalRowAmount(res?.data?.total_amount);
      setTotalRowNetAmount(res?.data?.total_net_amount);
      setTotalRowTaxAmount(res?.data?.total_other_tax_amount);
      setTotalRowWhtAmount(res?.data?.total_wht_amount);

      setTimeout(() => {
        handleOnlyPrint();
      }, 200);

      setIsFetchingData(false);
    } catch (error) {
      setIsFetchingData(false);
    }
  };

  const handleViewLedger = async (id) => {
    setIsFetchingData(true);
    const url =
      BASE_URL + "/settings/check-financial-ledger-view/" + id + "/BR";
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setAccountLedger({
          modal: true,
          ledger: res.data,
        });
      }
      setIsFetchingData(false);
    } catch (error) {
      setIsFetchingData(false);
    }
  };

  //delete account here
  const handleDeleteAccount = (slug) => {
    setIsFetchingData(true);
    const bplUrl = BASE_URL + `/settings/bank-receipt/${slug}`;
    return axios
      .delete(bplUrl, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        setBankPaymentReceiptList(res.data[0]);
        setBankPaymentReceiptListForSearch(res.data[1]);
        setSearchedBPList({
          ...searchedBPList,
          list: res.data[1],
        });
        setIsFetchingData(false);
        ToastSuccess({ title: t("Receipt has been deleted successfully") });
      })
      .catch((error) => {
        setIsFetchingData(false);
        ToastError({ title: t("Please try again") });
      });
  };

  //search account here
  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedBPList({ ...searchedBPList, searched: false });
    } else {
      let searchedList = bankPaymentReceiptListForSearch.filter((item) => {
        let lowerCaseItemIdentity = item.document_identity.toLowerCase();
        let lowerCaseItemTotal = item.total_net_amount.toLowerCase();
        let lowerCaseItemDate = moment(item.document_date)
          .format("LL")
          .toLowerCase();
        const lowerCaseCustomer =
          item.customer && item?.customer?.name.toLowerCase();
        return (
          lowerCaseItemIdentity.includes(searchInput) ||
          lowerCaseItemTotal.includes(searchInput) ||
          (lowerCaseCustomer && lowerCaseCustomer.includes(searchInput)) ||
          (lowerCaseItemDate && lowerCaseItemDate.includes(searchInput))
        );
      });
      setSearchedBPList({
        ...searchedBPList,
        list: searchedList,
        searched: true,
      });
    }
  };

  //delete confirmation modal of account
  const handleDeleteConfirmation = (slug) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{t("Are you sure?")}</h1>
            <p className="text-center">{t("You want to delete this?")}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary rounded"
                onClick={() => {
                  handleDeleteAccount(slug);
                  onClose();
                }}
              >
                {t("Yes, delete it!")}
              </button>
              <button
                className="btn btn-success ml-2 px-3 rounded"
                onClick={onClose}
              >
                {t("No")}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handlePostApi = async (item) => {
    setActionLoading(true);
    const url = BASE_URL + "/settings/bank-receipt-post";
    let formData = new FormData();
    formData.append("id", item.id);
    formData.append("is_post", item.is_post ? 0 : 1);
    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setBankPaymentReceiptList(res.data[0]);
        setBankPaymentReceiptListForSearch(res.data[1]);
        setSearchedBPList({
          ...searchedBPList,
          list: res.data[1],
        });
        ToastSuccess({
          title: t(
            item.is_post
              ? "Bank receipt has been unpost"
              : "Bank receipt has been post"
          ),
        });
      }
      setActionLoading(false);
    } catch {
      setActionLoading(false);
      ToastError({ title: t("Please try again") });
    }
  };

  //unpost confirmation modal
  const handleUnPostConfirmation = (item) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body px-5">
            <h3>{t("Are you sure?")}</h3>
            <p className="text-center">{t("You want to un post this?")}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handlePostApi(item);
                  onClose();
                }}
              >
                {t("Yes, unpost it!")}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {t("No")}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handleFetchData = (page) => {
    getBPReciptList(page);
  };

  const COLUMNS = [
    { label: "S/L", render: (item, i) => i + 1 },
    { label: "Document No", key: "document_identity" },
    {
      label: "Document Date",
      render: (item) => <Moment format="LL">{item.document_date}</Moment>,
    },
    { label: "Branch", render: (item) => item.branch?.name },
    { label: "Customer", render: (item) => item.customer?.name || "-" },
    { label: "Total", key: "total_net_amount" },
    {
      label: "Action",
      render: (item) => (
        <div className="d-flex justify-content-center">
          {item.is_post !== 1 && (
            <>
              <NavLink
                title="Edit"
                className="btn btn-primary ml-2 rounded-sm del-btn"
                to={`/dashboard/account/transaction/bank-receipt-edit/${item.id}`}
              >
                <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
              </NavLink>
              <button
                title="Delete"
                className="btn btn-danger ml-2 rounded-sm del-btn"
                onClick={() => handleDeleteConfirmation(item.id)}
              >
                <MdDelete className="del-icon" />
              </button>
            </>
          )}
          <button
            title="Print"
            className="btn btn-secondary ml-2 rounded-sm del-btn"
            onClick={() => handlePrint(item)}
          >
            <i className="fa fa-print edit-icon"></i>
          </button>
          {item.is_post !== 1 ? (
            <button
              className="btn btn-dark ml-2 rounded-sm del-btn"
              title="Post Bank Receipt"
              style={{ padding: "1px 0px 0px 1px" }}
              disabled={actionLoading}
              onClick={() => handlePostApi(item)}
            >
              <i className="fa fa-check-square-o" aria-hidden="true"></i>
            </button>
          ) : null}
          {authUserInfo?.details?.user_type === "superAdmin" &&
          item.is_post === 1 ? (
            <button
              className="btn btn-warning ml-2 rounded-sm del-btn"
              title="Unpost Bank Receipt"
              style={{ padding: "1px 0px 0px 1px" }}
              disabled={actionLoading}
              onClick={() => handleUnPostConfirmation(item)}
            >
              <i className="fa fa-check-square-o" aria-hidden="true"></i>
            </button>
          ) : null}
          {item.is_post === 1 && (
            <button
              title="View ledger"
              className="btn btn-success ml-2 rounded-sm del-btn"
              onClick={() => handleViewLedger(item.id)}
            >
              <i className="fa fa-eye"></i>
            </button>
          )}
        </div>
      ),
    },
  ];

  const getListData = useCallback(() => {
    if (searchedBPList.searched) {
      return searchedBPList.list;
    }

    return bankPaymentReceiptList?.data || [];
  }, [searchedBPList, bankPaymentReceiptList]);

  const onClearSearch = () => {
    setSearchedBPList({ ...searchedBPList, searched: false });
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  return (
    <>
      <Helmet>
        <title>{t("Bank Receipt List")}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <AccountsSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="">
                <div key="fragment3">
                  {/* {Table Header} */}
                  <div className="t-bg-white">
                    <div className="row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                      <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                        <ul className="t-list fk-breadcrumb">
                          <li className="fk-breadcrumb__list">
                            <span className="t-link fk-breadcrumb__link text-capitalize">
                              {!searchedBPList.searched
                                ? t("Bank Receipt List")
                                : t("Search Result")}
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div className="col-md-6 col-lg-7">
                        <div className="row gx-3 align-items-center">
                          {/* Search group */}
                          <div className="col-md-9 t-mb-15 mb-md-0">
                            <div className="input-group">
                              <div className="form-file">
                                <input
                                  type="text"
                                  className="form-control border-0 form-control--light-1 rounded-left"
                                  placeholder={t("Search") + ".."}
                                  onChange={handleSearch}
                                  disabled={isFetchingData}
                                  ref={searchInputRef}
                                />
                              </div>
                              <button
                                className="btn btn-primary rounded-right"
                                type="button"
                              >
                                <i
                                  className="fa fa-search"
                                  aria-hidden="true"
                                ></i>
                              </button>
                            </div>
                          </div>

                          {/* Add group modal trigger button */}
                          <div className="col-md-3 text-md-right">
                            <NavLink
                              to="/dashboard/account/transaction/bank-receipt-add"
                              className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                            >
                              {t("add new")}
                            </NavLink>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <DataTable
                    loading={actionLoading || isFetchingData}
                    data={getListData()}
                    columns={COLUMNS}
                    emptyText="No data available"
                    pagination={{
                      isPageLoading: actionLoading,
                      isPagination: true,
                      pageCount: bankPaymentReceiptList?.last_page,
                      currentPage: bankPaymentReceiptList?.current_page,
                      from: bankPaymentReceiptList?.from,
                      to: bankPaymentReceiptList?.to,
                      total: bankPaymentReceiptList?.total,
                    }}
                    handleFetchData={handleFetchData}
                    searchPayload={{
                      isSearched: searchedBPList.searched,
                      onClearSearch,
                    }}
                  />
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
      {/* main body ends */}
      <AccountPrint
        name="Bank Receipt"
        newRow={newRow}
        bankPayment={bankPayment}
        totalRowAmount={totalRowAmount}
        totalRowWhtAmount={totalRowWhtAmount}
        totalRowTaxAmount={totalRowTaxAmount}
        totalRowNetAmount={totalRowNetAmount}
        AccountPrint={componentRef}
      />
      <ViewAccountSheet
        accountLedger={accountLedger}
        setAccountLedger={setAccountLedger}
      />
    </>
  );
};

export default BankReceipt;
